
import { BillingRecord, BankDeposit, MatchStatus } from '../types';

const utilsCode = `
const HALF_WIDTH_KANA_MAP = {
  'ｶﾞ': 'ガ', 'ｷﾞ': 'ギ', 'ｸﾞ': 'グ', 'ｹﾞ': 'ゲ', 'ｺﾞ': 'ゴ',
  'ｻﾞ': 'ザ', 'ｼﾞ': 'ジ', 'ｽﾞ': 'ズ', 'ｾﾞ': 'ゼ', 'ｿﾞ': 'ゾ',
  'ﾀﾞ': 'ダ', 'ﾁﾞ': 'ヂ', 'ﾂﾞ': 'ヅ', 'ﾃﾞ': 'デ', 'ﾄﾞ': 'ド',
  'ﾊﾞ': 'バ', 'ﾋﾞ': 'ビ', 'ﾌﾞ': 'ブ', 'ﾍﾞ': 'ベ', 'ﾎﾞ': 'ボ',
  'ﾊﾟ': 'パ', 'ﾋﾟ': 'ピ', 'ﾌﾟ': 'プ', 'ﾍﾟ': 'ペ', 'ﾎﾟ': 'ポ',
  'ｳﾞ': 'ヴ',
  'ｱ': 'ア', 'ｲ': 'イ', 'ｳ': 'ウ', 'ｴ': 'エ', 'ｵ': 'オ',
  'ｶ': 'カ', 'ｷ': 'キ', 'ｸ': 'ク', 'ｹ': 'ケ', 'ｺ': 'コ',
  'ｻ': 'サ', 'ｼ': 'シ', 'ｽ': 'ス', 'ｾ': 'セ', 'ｿ': 'ソ',
  'ﾀ': 'タ', 'ﾁ': 'チ', 'ﾂ': 'ツ', 'ﾃ': 'テ', 'ﾄ': 'ト',
  'ﾅ': 'ナ', 'ﾆ': 'ニ', 'ﾇ': 'ヌ', 'ﾈ': 'ネ', 'ﾉ': 'ノ',
  'ﾊ': 'ハ', 'ﾋ': 'ヒ', 'ﾌ': 'フ', 'ﾍ': 'ヘ', 'ﾎ': 'ホ',
  'ﾏ': 'マ', 'ﾐ': 'ミ', 'ﾑ': 'ム', 'ﾒ': 'メ', 'ﾓ': 'モ',
  'ﾔ': 'ヤ', 'ﾕ': 'ユ', 'ﾖ': 'ヨ', 'ﾗ': 'ラ', 'ﾘ': 'リ', 'ﾙ': 'ル', 'ﾚ': 'レ', 'ﾛ': 'ロ', 'ﾜ': 'ワ', 'ｦ': 'ヲ', 'ﾝ': 'ン',
  'ｧ': 'ア', 'ｨ': 'イ', 'ｩ': 'ウ', 'ｪ': 'エ', 'ｫ': 'オ',
  'ｯ': 'ツ', 'ｬ': 'ヤ', 'ｭ': 'ユ', 'ｮ': 'ヨ',
  '｡': '。', '｢': '「', '｣': '」', '､': '、', '･': '・', 'ｰ': '', 'ー': '',
  'ﾞ': '', 'ﾟ': '' 
};

function escapeRegExp(string) {
  // テンプレートリテラル内での変数展開(\${})と誤認されるのを防ぐため、
  // $記号を \\x24 (16進数エスケープ) で記述する。機能的には $ と等価。
  return string.replace(/[.*+?^\\x24{}()|[\\]\\\\]/g, '\\\\$&');
}

function toFullWidthKana(str) {
  if (!str) return '';
  let val = String(str);
  const keys = Object.keys(HALF_WIDTH_KANA_MAP).sort((a, b) => b.length - a.length);
  const escapedKeys = keys.map(k => escapeRegExp(k));
  const reg = new RegExp('(' + escapedKeys.join('|') + ')', 'g');
  val = val.replace(reg, (match) => HALF_WIDTH_KANA_MAP[match]);
  return val;
}

/**
 * 比較用正規化（法人格を除去する・広範なマッチング用）
 */
function robustNormalize(input) {
  if (!input) return '';
  let val = toFullWidthKana(input);
  // 異体字・旧字体・CJK互換文字の統一
  val = val.replace(/濵/g, '浜').replace(/濱/g, '浜').replace(/髙/g, '高').replace(/﨑/g, '崎');
  val = val.replace(/國/g, '国').replace(/㐂/g, '喜').replace(/靑/g, '青').replace(/纊/g, '絋');
  val = val.replace(/⻄/g, '西').replace(/⻑/g, '長').replace(/⻘/g, '青').replace(/⻝/g, '食');
  val = val.replace(/鷗/g, '鴎').replace(/櫻/g, '桜').replace(/齋/g, '斎');
  val = val.replace(/澤/g, '沢').replace(/邊/g, '辺').replace(/邉/g, '辺').replace(/廣/g, '広');
  val = val.replace(/與/g, '与').replace(/龍/g, '竜').replace(/寶/g, '宝').replace(/實/g, '実');
  val = val.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0)).toUpperCase();
  const smallToLarge = {
    'ァ': 'ア', 'ィ': 'イ', 'ゥ': 'ウ', 'ェ': 'エ', 'ォ': 'オ',
    'ッ': 'ツ', 'ャ': 'ヤ', 'ュ': 'ユ', 'ョ': 'ヨ'
  };
  val = val.replace(/[ァ-ョッ]/g, (s) => smallToLarge[s] || s);

  // カッコの統一
  val = val.replace(/\\(/g, '（').replace(/\\)/g, '）');

  // 記号除去（カッコは残す）
  val = val.replace(/[\\s\\u3000\\-\\.・\\/,，、。\\*？?！!ｰ－]/g, '');
  
  // 法人格除去 (カッコ付きのみ対象とし、単体のカタカナ誤爆を防ぐ)
  const noise = [
    '株式会社', '有限会社', '合同会社', '合資会社', '一般社団法人', '財団法人',
    'カブシキガイシャ', 'ユウゲンガイシャ', 'ドウドウガイシャ',
    '（カ）', '（ユ）', '（ド）', '（シ）', '（キ）', '（ザイ）', '（シャ）',
    '（株）', '（有）', '（合）', '（資）', '（財）', '（社）',
    '（カ', '（ユ', '（ド',
    'カ）', 'ユ）', 'ド）'
  ];
  const noisePattern = noise.map(escapeRegExp).join('|');
  const noiseRegExp = new RegExp(noisePattern, 'g');
  
  val = val.replace(noiseRegExp, '');
  
  // 最後に残ったカッコを除去
  val = val.replace(/[（）]/g, '');

  // 支店・工場・出張所などの拠点名とその前の地名部分を除去
  // ※除去後に2文字未満になる場合は除去しない（会社名ごと消える問題の防止）
  var branchStripped = val.replace(/[^\\s]*?(工場|出張所|支店|営業所|支所|事業所|事業部|製造所|プラント).*$/g, '');
  if (branchStripped.length >= 2) val = branchStripped;

  return val;
}

/**
 * 厳密な判定用正規化（法人格を残す）
 */
function simpleNormalize(input) {
  if (!input) return '';
  let val = toFullWidthKana(input);
  val = val.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0)).toUpperCase();
  const smallToLarge = {
    'ァ': 'ア', 'ィ': 'イ', 'ゥ': 'ウ', 'ェ': 'エ', 'ォ': 'オ',
    'ッ': 'ツ', 'ャ': 'ヤ', 'ュ': 'ユ', 'ョ': 'ヨ'
  };
  val = val.replace(/[ァ-ョッ]/g, (s) => smallToLarge[s] || s);
  // 記号は削除するが、法人格キーワードは削除しない
  val = val.replace(/[-\\s\\u3000\\(\\)\\（\\）\\uFF0Dー・.\\/\\* :;,，、。！？!?]/g, '');
  return val;
}

function getSimilarityScore(str1, str2) {
    if (!str1 || !str2) return 0;
    const n1 = robustNormalize(str1);
    const n2 = robustNormalize(str2);
    if (!n1 || !n2) return 0;
    
    if (n1 === n2) return 100;
    const minLen = Math.min(n1.length, n2.length);
    const maxLen = Math.max(n1.length, n2.length);
    if (minLen >= 4 && minLen >= maxLen * 0.4) {
        if (n1.includes(n2) || n2.includes(n1)) return 95;
    }
    if (minLen >= 4 && n1.substring(0, minLen) === n2.substring(0, minLen)) return 92;

    let matchCount = 0;
    for (let i = 0; i < minLen; i++) {
        if (n1[i] === n2[i]) matchCount++;
    }
    const ratio = (matchCount / maxLen) * 100;
    if (ratio >= 89) return Math.floor(ratio);

    return 0;
}

const FEE_TOLERANCE_MAX = 1000;

/**
 * 入金日と請求月の月差を返す（正の値 = 請求が入金より前）
 * 翌月払いなら月差=1が理想。月差が大きいほど古い請求。
 */
function billingMonthDistance(depositDate, billingImportMonth) {
  if (!depositDate || !billingImportMonth) return 99;
  const depYM = String(depositDate).replace(/-/g, '/').substring(0, 7);
  const billYM = String(billingImportMonth).replace(/-/g, '/').substring(0, 7);
  if (!depYM || !billYM) return 99;
  const [dy, dm] = depYM.split('/').map(Number);
  const [by, bm] = billYM.split('/').map(Number);
  return (dy * 12 + dm) - (by * 12 + bm);
}

/**
 * 入金日・請求月・支払サイクルから、照合対象として有効な月かを判定する
 * paymentCycle=1（翌月払い）: 前月(1) の請求のみ許容
 * paymentCycle=2（翌々月払い）: 1〜2ヶ月前(1-2) の請求を許容
 * paymentCycle=3（翌々々月払い）: 1〜3ヶ月前(1-3) の請求を許容
 * ※同月(0)は自動照合の対象外。翌月払い前提のため入金月と同月の請求書への
 *   自動照合は行わない（手動照合のみで対応）。
 */
function isMonthAllowed(depositDate, billingImportMonth, paymentCycle) {
  const dist = billingMonthDistance(depositDate, billingImportMonth);
  return dist >= 1 && dist <= (paymentCycle || 1);
}

/**
 * 2つの請求候補を比較し、より適切な方を返す
 * 優先順位: ①入金月の1ヶ月前に近い請求月（前月払い前提）②金額差が小さい
 */
function pickBestCandidate(prev, curr, depAmt, depositDate) {
  // 月距離を優先判定（1ヶ月前が理想、2ヶ月前は翻々月払い想定）
  const prevDist = Math.abs(billingMonthDistance(depositDate, prev.import_month) - 1);
  const currDist = Math.abs(billingMonthDistance(depositDate, curr.import_month) - 1);
  if (currDist !== prevDist) return currDist < prevDist ? curr : prev;
  // 同じ月距離の場合は金額差で判断
  const prevDiff = Math.abs(Number(prev.billing_balance) - depAmt);
  const currDiff = Math.abs(Number(curr.billing_balance) - depAmt);
  return currDiff < prevDiff ? curr : prev;
}
`;

export const runAutoMatchingWorker = async (
  billing: BillingRecord[],
  deposits: BankDeposit[],
  existingMatches: any[],
  autoRules: any[],
  recurringRules: any[],
  paymentCycleMap: { [clientCode: string]: number } = {},
  blockedCombinations: string[] = []
): Promise<{ matches: any[], count: number }> => {
  return new Promise((resolve, reject) => {
    
    const matchStatusCodes = `
      const STATUS_EXACT = '${MatchStatus.EXACT}';
      const STATUS_WITH_FEE = '${MatchStatus.WITH_FEE}';
      const STATUS_NEEDS_REVIEW = '${MatchStatus.NEEDS_REVIEW}';
      const STATUS_OTHER = '${MatchStatus.OTHER}';
      const STATUS_UNMATCHED = '${MatchStatus.UNMATCHED}';
    `;

    const mainLogic = `
      self.onmessage = (e) => {
        try {
          const { billing, deposits, existingMatches, autoRules, recurringRules, paymentCycleMap, blockedCombinations } = e.data;
          // 紐付け解除履歴：過去に解除された組み合わせ（billingKey + depositKey）を Set で保持
          const blockedSet = new Set(blockedCombinations || []);
          // 支払サイクル取得ヘルパー（未登録の場合はデフォルト1＝翌月払い）
          const getPaymentCycle = (clientCode) => (paymentCycleMap && clientCode && paymentCycleMap[clientCode]) || 1;
          const newMatches = [];
          let matchCount = 0;
          
          const usedBillingIds = new Set(existingMatches.map(m => m.billing_id).filter(id => id));
          const usedDepositIds = new Set(existingMatches.map(m => m.deposit_id).filter(id => id));

          // 手動照合で解除したマッチ（UNMATCHED扱い）は使用済みIDから除外
          existingMatches.forEach(m => {
              if (m.match_type === STATUS_UNMATCHED && m.billing_id && m.deposit_id) {
                  if (usedBillingIds.has(m.billing_id)) usedBillingIds.delete(m.billing_id);
                  if (usedDepositIds.has(m.deposit_id)) usedDepositIds.delete(m.deposit_id);
              }
          });

          const activeDeposits = deposits.filter(d =>
              !d.is_other && !usedDepositIds.has(d.id)
          );

          // 得意先コードごとの全請求データ数を集計（複数請求判定用）
          const billingCountByClient = {};
          billing.forEach(b => {
              billingCountByClient[b.client_code] = (billingCountByClient[b.client_code] || 0) + 1;
          });
          // 得意先コードごとの未使用請求数（マッチされていない請求の数）
          const unusedBillingCountByClient = {};
          billing.forEach(b => {
              if (!usedBillingIds.has(b.id)) {
                  unusedBillingCountByClient[b.client_code] = (unusedBillingCountByClient[b.client_code] || 0) + 1;
              }
          });
          // 既にマッチ済みの得意先コード（既存マッチ + 今回マッチ分）
          const matchedClientCodes = new Set();
          existingMatches.forEach(m => {
              if (m.client_code && m.match_type !== STATUS_UNMATCHED) {
                  matchedClientCodes.add(m.client_code);
              }
          });

          activeDeposits.forEach(deposit => {
            const depAmt = Number(deposit.amount);

            // 0. 顧客コード特定済み、または「でんさい」データの場合の処理 (最優先)
            if (deposit.client_code || deposit.is_densai) {
                 let matchedWithBilling = false;

                 // A. client_codeがある場合は、まず請求データとの紐付けを試みる
                 if (deposit.client_code) {
                     const candidates = billing.filter(b => {
                         if (usedBillingIds.has(b.id)) return false;
                         if (b.client_code !== deposit.client_code) return false;
                         // でんさいは支払期日基準のため月制約なし・金額厳密チェックのみ
                         if (deposit.is_densai) {
                             return Math.abs(Number(b.billing_balance) - depAmt) <= FEE_TOLERANCE_MAX;
                         }
                         // 月制約フィルタ（支払サイクルに基づく）
                         return isMonthAllowed(deposit.deposit_date, b.import_month, getPaymentCycle(deposit.client_code));
                     });

                     if (candidates.length > 0) {
                         // 複数請求判定: 同一得意先に複数の請求があり、既にマッチ済みの場合は要確認
                         const clientCode = deposit.client_code;
                         const totalBillingsForClient = billingCountByClient[clientCode] || 0;
                         const isMultiBilling = totalBillingsForClient > 1 && (matchedClientCodes.has(clientCode) || (unusedBillingCountByClient[clientCode] || 0) >= 2);

                         if (isMultiBilling) {
                             const bestCandidateMulti = candidates.reduce((prev, curr) => {
                                return pickBestCandidate(prev, curr, depAmt, deposit.deposit_date);
                             });
                             const multiDiff = Math.abs(Number(bestCandidateMulti.billing_balance) - depAmt);
                             const multiType = multiDiff === 0 ? STATUS_EXACT : multiDiff <= FEE_TOLERANCE_MAX ? STATUS_WITH_FEE : STATUS_NEEDS_REVIEW;
                             newMatches.push({
                                 billing_id: bestCandidateMulti.id,
                                 deposit_id: deposit.id,
                                 client_code: clientCode,
                                 match_type: multiType,
                                 manually_approved: false,
                                 notes: multiType === STATUS_NEEDS_REVIEW ? '同一得意先に複数の請求あり（追加入金 or 別請求の判断が必要）' : '',
                                 multi_billing_client: multiType === STATUS_NEEDS_REVIEW,
                                 candidate_codes: multiType === STATUS_NEEDS_REVIEW ? [clientCode] : undefined
                             });
                             matchCount++; usedDepositIds.add(deposit.id);
                             usedBillingIds.add(bestCandidateMulti.id);
                             matchedClientCodes.add(clientCode);
                             matchedWithBilling = true;
                         } else {
                             const bestCandidate = candidates.reduce((prev, curr) => {
                                return pickBestCandidate(prev, curr, depAmt, deposit.deposit_date);
                             });

                             const billAmt = Number(bestCandidate.billing_balance);
                             const diff = Math.abs(billAmt - depAmt);

                             let matchType = STATUS_NEEDS_REVIEW;
                             if (diff === 0) matchType = STATUS_EXACT;
                             else if (diff <= FEE_TOLERANCE_MAX) matchType = STATUS_WITH_FEE;

                             newMatches.push({
                                 billing_id: bestCandidate.id,
                                 deposit_id: deposit.id,
                                 client_code: bestCandidate.client_code,
                                 match_type: matchType,
                                 manually_approved: false,
                                 notes: deposit.is_densai ? 'でんさい予約照合' : 'コード照合'
                             });
                             usedBillingIds.add(bestCandidate.id); matchCount++; usedDepositIds.add(deposit.id);
                             matchedClientCodes.add(bestCandidate.client_code);
                             matchedWithBilling = true;
                         }
                     }
                 }

                 // B. 請求データと紐付かなかった場合
                 if (!matchedWithBilling) {
                     // 「でんさい」データであり、かつ取込時に予約データとの整合性（金額・名称・期日）が確認されている場合
                     if (deposit.is_densai) {
                         // 請求データが存在しなくても、でんさい予約との照合を正として「完全一致」とする
                         newMatches.push({
                             deposit_id: deposit.id,
                             match_type: STATUS_EXACT,
                             client_code: deposit.client_code || '',
                             notes: 'でんさい予約照合済（請求データなし）',
                             manually_approved: false
                         });
                         matchCount++; usedDepositIds.add(deposit.id); return;
                     }
                 }
                 
                 // でんさいでなく、かつ請求も見つからず、client_codeがある場合
                 // ここでは処理を終了せず、後続のロジック（自動振分など）は通さない。
                 // コードがあって請求がない場合は「未照合」のままで良い（変に自動マッチさせない）
                 if (deposit.client_code) {
                     return;
                 }
            }

            // 1. 自動振分ルール
            if (autoRules && autoRules.length > 0) {
                const rule = autoRules.find(r => getSimilarityScore(r.remitter_name, deposit.remitter_name) >= 95);

                if (rule) {
                    const ruleAmt = Number(rule.amount);
                    const isAmountMatched = (ruleAmt === 0 || Math.abs(ruleAmt - depAmt) <= 10);

                    if (rule.client_code) {
                         const candidates = billing.filter(b => {
                             if (b.client_code !== rule.client_code) return false;
                             if (usedBillingIds.has(b.id)) return false;
                             return isMonthAllowed(deposit.deposit_date, b.import_month, getPaymentCycle(rule.client_code));
                         });
                         if (candidates.length > 0) {
                             // 複数請求判定
                             const totalBillingsForClient = billingCountByClient[rule.client_code] || 0;
                             const isMultiBilling = totalBillingsForClient > 1 && (matchedClientCodes.has(rule.client_code) || (unusedBillingCountByClient[rule.client_code] || 0) >= 2);

                             if (isMultiBilling) {
                                 const bestCandidateMulti = candidates.reduce((prev, curr) => {
                                    return pickBestCandidate(prev, curr, depAmt, deposit.deposit_date);
                                 });
                                 const multiDiff = Math.abs(Number(bestCandidateMulti.billing_balance) - depAmt);
                                 const multiType = multiDiff === 0 ? STATUS_EXACT : multiDiff <= FEE_TOLERANCE_MAX ? STATUS_WITH_FEE : STATUS_NEEDS_REVIEW;
                                 newMatches.push({
                                     billing_id: bestCandidateMulti.id,
                                     deposit_id: deposit.id,
                                     client_code: rule.client_code,
                                     client_name: rule.client_name,
                                     match_type: multiType,
                                     manually_approved: false,
                                     notes: multiType === STATUS_NEEDS_REVIEW ? '同一得意先に複数の請求あり（追加入金 or 別請求の判断が必要）' : '自動振分ルール（学習）による特定',
                                     multi_billing_client: multiType === STATUS_NEEDS_REVIEW,
                                     candidate_codes: multiType === STATUS_NEEDS_REVIEW ? [rule.client_code] : undefined
                                 });
                                 usedDepositIds.add(deposit.id); usedBillingIds.add(bestCandidateMulti.id); matchCount++; matchedClientCodes.add(rule.client_code); return;
                             }

                             const bestCandidate = candidates.reduce((prev, curr) => {
                                return pickBestCandidate(prev, curr, depAmt, deposit.deposit_date);
                             });
                             const billAmt = Number(bestCandidate.billing_balance);
                             const diff = Math.abs(billAmt - depAmt);
                             let matchType = STATUS_NEEDS_REVIEW;
                             if (diff === 0) matchType = STATUS_EXACT;
                             else if (diff <= FEE_TOLERANCE_MAX) matchType = STATUS_WITH_FEE;

                             newMatches.push({
                                 billing_id: bestCandidate.id,
                                 deposit_id: deposit.id,
                                 client_code: bestCandidate.client_code,
                                 match_type: matchType,
                                 manually_approved: false,
                                 notes: '自動振分ルール（学習）による特定'
                             });
                             usedBillingIds.add(bestCandidate.id); usedDepositIds.add(deposit.id); matchCount++;
                             matchedClientCodes.add(bestCandidate.client_code); return;
                         }
                    }
                    if (isAmountMatched) {
                        newMatches.push({ 
                            deposit_id: deposit.id, 
                            match_type: STATUS_OTHER, 
                            notes: '自動振分ルール適用: ' + (rule.notes || ''), 
                            manually_approved: false, 
                            client_code: rule.client_code,
                            client_name: rule.client_name 
                        });
                        usedDepositIds.add(deposit.id); matchCount++; return;
                    }
                    if (rule.client_code) {
                         newMatches.push({ 
                            deposit_id: deposit.id, 
                            match_type: STATUS_NEEDS_REVIEW, 
                            notes: '自動振分ルールにより顧客特定（請求なし）', 
                            manually_approved: false, 
                            client_code: rule.client_code,
                            client_name: rule.client_name 
                        });
                        usedDepositIds.add(deposit.id); matchCount++; return;
                    }
                }
            }

            // 1.5. 振込人名からの顧客コード推論
            const codeMatch = deposit.remitter_name ? deposit.remitter_name.match(/(\\d{4,8})/) : null;
            if (codeMatch) {
                const extractedCode = codeMatch[1];
                const codeCandidates = billing.filter(b => {
                    if (usedBillingIds.has(b.id)) return false;
                    if (b.client_code !== extractedCode) return false;
                    return isMonthAllowed(deposit.deposit_date, b.import_month, getPaymentCycle(extractedCode));
                });

                if (codeCandidates.length > 0) {
                    // 複数請求判定
                    const totalBillingsForClient = billingCountByClient[extractedCode] || 0;
                    const isMultiBilling = totalBillingsForClient > 1 && (matchedClientCodes.has(extractedCode) || (unusedBillingCountByClient[extractedCode] || 0) >= 2);

                    if (isMultiBilling) {
                        const bestCandidateMulti = codeCandidates.reduce((prev, curr) => {
                            return pickBestCandidate(prev, curr, depAmt, deposit.deposit_date);
                        });
                        const multiDiff = Math.abs(Number(bestCandidateMulti.billing_balance) - depAmt);
                        const multiType = multiDiff === 0 ? STATUS_EXACT : multiDiff <= FEE_TOLERANCE_MAX ? STATUS_WITH_FEE : STATUS_NEEDS_REVIEW;
                        newMatches.push({
                            billing_id: bestCandidateMulti.id,
                            deposit_id: deposit.id,
                            client_code: extractedCode,
                            match_type: multiType,
                            manually_approved: false,
                            notes: multiType === STATUS_NEEDS_REVIEW ? '同一得意先に複数の請求あり（追加入金 or 別請求の判断が必要）' : 'コード推論（振込人名から抽出）',
                            multi_billing_client: multiType === STATUS_NEEDS_REVIEW,
                            candidate_codes: multiType === STATUS_NEEDS_REVIEW ? [extractedCode] : undefined
                        });
                        usedDepositIds.add(deposit.id); usedBillingIds.add(bestCandidateMulti.id); matchCount++; matchedClientCodes.add(extractedCode); return;
                    }

                    const bestCandidate = codeCandidates.reduce((prev, curr) => {
                        return pickBestCandidate(prev, curr, depAmt, deposit.deposit_date);
                    });

                    const billAmt = Number(bestCandidate.billing_balance);
                    const diff = Math.abs(billAmt - depAmt);

                    let matchType = STATUS_NEEDS_REVIEW;
                    if (diff === 0) matchType = STATUS_EXACT;
                    else if (diff <= FEE_TOLERANCE_MAX) matchType = STATUS_WITH_FEE;

                    newMatches.push({
                        billing_id: bestCandidate.id,
                        deposit_id: deposit.id,
                        client_code: bestCandidate.client_code,
                        match_type: matchType,
                        manually_approved: false,
                        notes: 'コード推論（振込人名から抽出）'
                    });
                    usedBillingIds.add(bestCandidate.id);
                    matchCount++;
                    usedDepositIds.add(deposit.id);
                    matchedClientCodes.add(bestCandidate.client_code);
                    return;
                }
            }

            // 2. 毎月振込ルール
            if (recurringRules && recurringRules.length > 0) {
                const rule = recurringRules.find(r => getSimilarityScore(r.remitter_name, deposit.remitter_name) >= 90);
                if (rule) {
                    const expectedAmt = Number(rule.expected_amount || 0);
                    const isExpectedAmountMatched = expectedAmt > 0 
                            ? Math.abs(expectedAmt - depAmt) <= FEE_TOLERANCE_MAX 
                            : true;

                    if (isExpectedAmountMatched) {
                        const candidates = billing.filter(b => {
                            if (b.client_code !== rule.client_code) return false;
                            if (usedBillingIds.has(b.id)) return false;
                            return isMonthAllowed(deposit.deposit_date, b.import_month, getPaymentCycle(rule.client_code));
                        });

                        // 毎月振込ルールでは「複数請求判定」を行わない
                        // 業務担当者が明示的にルールを設定しているため、要確認に振り分けず
                        // 通常の1000円誤差ルール（金額一致なら紐付け、なければ請求データなし）に従う
                        const bestCandidate = candidates.length > 0 ? candidates.reduce((prev, curr) => {
                            return pickBestCandidate(prev, curr, depAmt, deposit.deposit_date);
                        }) : null;
                        const billDiff = bestCandidate ? Math.abs(Number(bestCandidate.billing_balance) - depAmt) : Infinity;
                        if (bestCandidate && billDiff <= FEE_TOLERANCE_MAX) {
                            newMatches.push({
                                billing_id: bestCandidate.id,
                                deposit_id: deposit.id,
                                client_code: bestCandidate.client_code,
                                match_type: billDiff === 0 ? STATUS_EXACT : STATUS_WITH_FEE,
                                manually_approved: false,
                                notes: '毎月振込設定適用'
                            });
                            usedBillingIds.add(bestCandidate.id); matchCount++; usedDepositIds.add(deposit.id);
                            matchedClientCodes.add(bestCandidate.client_code); return;
                        } else {
                             newMatches.push({ 
                                deposit_id: deposit.id, 
                                match_type: STATUS_EXACT, 
                                manually_approved: false, 
                                notes: '毎月振込設定適用（請求データなし）',
                                client_code: rule.client_code,
                                client_name: rule.client_name 
                            });
                            matchCount++; usedDepositIds.add(deposit.id); return;
                        }
                    }
                }
            }

            // 3. 名称推論（修正: 候補群抽出 -> 厳密判定 -> 確定）
            let candidates = [];
            
            billing.forEach(b => {
                if (usedBillingIds.has(b.id)) return;
                // 月制約フィルタ（請求側の得意先コードで支払サイクルを判定）
                if (!isMonthAllowed(deposit.deposit_date, b.import_month, getPaymentCycle(b.client_code))) return;

                const billAmt = Number(b.billing_balance);
                const diff = Math.abs(billAmt - depAmt);
                
                const scoreKana = getSimilarityScore(deposit.remitter_name, b.client_kana || '');
                const scoreName = getSimilarityScore(deposit.remitter_name, b.client_name || '');
                const scoreAbbr = getSimilarityScore(deposit.remitter_name, b.client_abbreviation || '');
                // 照合名キーワード（imported_names）との照合スコア
                const scoreImported = (b.imported_names || []).reduce((max, name) => {
                    return Math.max(max, getSimilarityScore(deposit.remitter_name, name));
                }, 0);
                const nameScore = Math.max(scoreKana, scoreName, scoreAbbr, scoreImported);
                
                // スコア70以上を候補とする。金額完全一致でもスコア30未満は除外
                if (nameScore >= 70 || (diff === 0 && nameScore >= 30)) {
                    candidates.push({ record: b, score: nameScore, diff: diff });
                }
            });

            if (candidates.length > 0) {
                // 1. スコア順にソート (降順)
                candidates.sort((a, b) => b.score - a.score);
                
                // 2. 最高スコアに近い候補のみ抽出（スコア差5以内）
                const maxScore = candidates[0].score;
                const topTier = candidates.filter(c => c.score >= maxScore - 5);
                
                let bestMatch = null;

                let isAmbiguous = false;
                let isMultiCode = false; // 名称完全一致・同名別コードの場合のみtrue
                if (topTier.length === 1) {
                    // 候補が1つならそれを採用
                    bestMatch = topTier[0];
                } else {
                    // 候補が複数の場合、厳密な判定を行う
                    const depSimple = simpleNormalize(deposit.remitter_name);

                    // 振込人名から法人格略号を方向別に除去（先頭: ユ）・カ）等、末尾: （ユ・（カ等）
                    // どちら側を除去したかを記録し、得意先カナの比較でも同じ方向のみ除去する
                    // （前株・後株の混同防止: カ）テスト → 前側のみ除去 → 後株テストカブシキガイシャは除外）
                    let remitterCore = deposit.remitter_name || '';
                    let strippedFromStart = false;
                    let strippedFromEnd = false;
                    const afterPrefixStrip = remitterCore.replace(/^[\u30A0-\u30FF\uFF65-\uFF9F][\uff09)]/g, '');
                    if (afterPrefixStrip !== remitterCore) { remitterCore = afterPrefixStrip; strippedFromStart = true; }
                    // 末尾の法人格略号を除去（閉じ括弧・ドット両対応: 例「（カ）」「（カ」「.カ」）
                    const afterSuffixStrip = remitterCore.replace(/[.\\uff08(][\u30A0-\u30FF\uFF65-\uFF9F][\uff09)]?$/, '');
                    if (afterSuffixStrip !== remitterCore) { remitterCore = afterSuffixStrip; strippedFromEnd = true; }
                    const depSimpleAlt = (strippedFromStart || strippedFromEnd) ? simpleNormalize(remitterCore) : null;

                    // simpleNormalize後の法人格カナパターン（濁点を保持した形）
                    // 得意先カナに法人格カナが含まれる場合に、振込人名と同じ方向のみ除去して比較する
                    const corpKanaNormalized = [
                        'ユウゲンガイシヤ',         // 有限会社（simpleNormalize後：ャ→ヤ）
                        'カブシキガイシヤ',         // 株式会社（simpleNormalize後：ャ→ヤ）
                        'ゴウドウガイシヤ',         // 合同会社（simpleNormalize後：ャ→ヤ）
                        'ゴウシガイシヤ',           // 合資会社（simpleNormalize後：ャ→ヤ）
                        'イツパンシヤダンホウジン', // 一般社団法人（simpleNormalize後：ャ→ヤ）
                        'ザイダンホウジン',         // 財団法人
                    ];

                    const matchAny = (n) => {
                        if (n === depSimple) return true;
                        if (depSimpleAlt !== null && n === depSimpleAlt) return true;
                        // 得意先カナに法人格カナが含まれる場合の方向別比較
                        if (depSimpleAlt !== null) {
                            for (const corp of corpKanaNormalized) {
                                if (strippedFromStart && n.startsWith(corp) && n.slice(corp.length) === depSimpleAlt) return true;
                                if (strippedFromEnd && n.endsWith(corp) && n.slice(0, -corp.length) === depSimpleAlt) return true;
                            }
                        }
                        // フォールバック: robustNormalize同士の完全一致
                        // 得意先カナに漢字法人格（例：有限会社サカエケンセツ）が含まれる場合に対応
                        const rn = robustNormalize(n);
                        const rd = robustNormalize(deposit.remitter_name);
                        if (rn && rd && rn === rd) return true;
                        return false;
                    };

                    // A. 法人格を含めた名称が一致するか確認（法人格略号除去後も試みる）
                    const strictMatches = topTier.filter(c => {
                        const r = c.record;
                        if (matchAny(simpleNormalize(r.client_name))) return true;
                        if (matchAny(simpleNormalize(r.client_kana || ''))) return true;
                        if (r.client_abbreviation && matchAny(simpleNormalize(r.client_abbreviation))) return true;
                        if ((r.imported_names || []).some(name => matchAny(simpleNormalize(name)))) return true;
                        return false;
                    });

                    if (strictMatches.length === 1) {
                        bestMatch = strictMatches[0];
                    } else if (strictMatches.length > 1) {
                        // 同名だが異なる得意先コードの候補があるか判定
                        const uniqueCodes = new Set(strictMatches.map(c => c.record.client_code));
                        if (uniqueCodes.size > 1) {
                            // 名称完全一致・異なる得意先コード → 同名別コード
                            strictMatches.sort((a, b) => a.diff - b.diff);
                            bestMatch = strictMatches[0];
                            isAmbiguous = true;
                            isMultiCode = true;
                        } else {
                            // 同一得意先コード → 金額差で選択
                            strictMatches.sort((a, b) => a.diff - b.diff);
                            bestMatch = strictMatches[0];
                        }
                    } else {
                        // B. 厳密一致がない場合（名称類似推論）、金額差が最も小さいものを採用
                        topTier.sort((a, b) => a.diff - b.diff);
                        bestMatch = topTier[0];
                        isAmbiguous = true;
                        // isMultiCode = false のまま（名称類似推論は昇格しない）
                    }
                }

                if (bestMatch) {
                    const bestCandidate = bestMatch.record;
                    const diff = bestMatch.diff;
                    const score = bestMatch.score;
                    const isHighConfidence = score >= 92;

                    // 複数請求判定: 同一得意先に複数の請求があり、既にマッチ済み
                    const totalBillingsForClient = billingCountByClient[bestCandidate.client_code] || 0;
                    const isMultiBilling = totalBillingsForClient > 1 && (matchedClientCodes.has(bestCandidate.client_code) || (unusedBillingCountByClient[bestCandidate.client_code] || 0) >= 2);

                    if (isMultiBilling && score >= 30) {
                        const multiType = diff === 0 ? STATUS_EXACT : diff <= FEE_TOLERANCE_MAX ? STATUS_WITH_FEE : STATUS_NEEDS_REVIEW;
                        newMatches.push({
                            billing_id: bestCandidate.id,
                            deposit_id: deposit.id,
                            client_code: bestCandidate.client_code,
                            match_type: multiType,
                            manually_approved: false,
                            notes: multiType === STATUS_NEEDS_REVIEW ? '同一得意先に複数の請求あり（追加入金 or 別請求の判断が必要）' : '',
                            multi_billing_client: multiType === STATUS_NEEDS_REVIEW,
                            candidate_codes: multiType === STATUS_NEEDS_REVIEW ? [bestCandidate.client_code] : undefined
                        });
                        matchCount++; usedDepositIds.add(deposit.id); usedBillingIds.add(bestCandidate.id);
                        matchedClientCodes.add(bestCandidate.client_code);
                    } else {
                        let matchType = STATUS_NEEDS_REVIEW;

                        if (diff === 0) {
                            matchType = isHighConfidence ? STATUS_EXACT : STATUS_NEEDS_REVIEW;
                        } else if (diff <= FEE_TOLERANCE_MAX) {
                            matchType = isHighConfidence ? STATUS_WITH_FEE : STATUS_NEEDS_REVIEW;
                        }

                        // 同名・別コード（名称完全一致）は差額で振り分け先を決定
                        // 名称類似推論（isAmbiguous=true かつ isMultiCode=false）は引き続き要確認
                        if (isAmbiguous) {
                            if (isMultiCode && diff <= FEE_TOLERANCE_MAX) {
                                // 名称完全一致・差額0円または1000円以下 → 照合済み（バッジ付きで識別可）
                                matchType = diff === 0 ? STATUS_EXACT : STATUS_WITH_FEE;
                            } else {
                                matchType = STATUS_NEEDS_REVIEW;
                            }
                        }

                        // スコアが30以上の場合のみマッチを作成
                        if (score >= 30) {
                            const ambiguousNote = isAmbiguous
                                ? '同名得意先が複数あり（得意先CD: ' + topTier.map(c => c.record.client_code).join(', ') + '）'
                                : '';
                            newMatches.push({
                                billing_id: bestCandidate.id,
                                deposit_id: deposit.id,
                                client_code: bestCandidate.client_code,
                                match_type: matchType,
                                manually_approved: false,
                                notes: ambiguousNote || (!isHighConfidence && diff !== 0 ? '名称類似による推論（要確認）' : '')
                            });
                            matchCount++;
                            usedBillingIds.add(bestCandidate.id);
                            usedDepositIds.add(deposit.id);
                            matchedClientCodes.add(bestCandidate.client_code);
                        }
                    }
                }
            }
          });

          // 紐付け解除履歴によるフィルター：再発防止
          // billing と deposit が両方存在する match のみが対象。片方しかない match はそのまま通す。
          const billingKeyById = {};
          billing.forEach(b => { if (b._blockingKey) billingKeyById[b.id] = b._blockingKey; });
          const depositKeyById = {};
          deposits.forEach(d => { if (d._blockingKey) depositKeyById[d.id] = d._blockingKey; });

          const filteredMatches = newMatches.filter(m => {
            if (!m.billing_id || !m.deposit_id) return true;
            const bKey = billingKeyById[m.billing_id];
            const dKey = depositKeyById[m.deposit_id];
            if (!bKey || !dKey) return true;
            return !blockedSet.has(bKey + '__' + dKey);
          });

          self.postMessage({ matches: filteredMatches, count: filteredMatches.length });
        } catch (err) {
          self.postMessage({ error: err.message });
        }
      };
    `;

    const workerCode = utilsCode + matchStatusCodes + mainLogic;
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));

    worker.onmessage = (e) => {
      if (e.data.error) {
          console.error('Worker internal error:', e.data.error);
          reject(new Error(e.data.error));
      } else {
          resolve(e.data);
      }
      worker.terminate();
    };

    worker.onerror = (e) => {
      console.error('Worker Script Error', e);
      reject(e);
      worker.terminate();
    };

    worker.postMessage({ billing, deposits, existingMatches, autoRules, recurringRules, paymentCycleMap, blockedCombinations });
  });
};
